<template>
    <div class="dashboard-block">
      <h2>{{ !booking.is_offer ? $t('Request') : `Offer` }} Details
        &nbsp;&nbsp;&nbsp;
        <button class="btn btn-plain-primary" v-if="!($userIsAdmin || $userIsIntern)" @click="handleDownloadPdfCallback">
          <span v-if="downloading" class="pickstar-loading"></span>
          <span v-else><i class="fa fa-cloud-download"></i>&nbsp;Download PDF</span>
        </button>
      </h2>

      <div class="form-item__row">
        <booking-field-input :booking="booking" :property-mutator="propertyMutator" field="name" :label="!booking.is_offer ? $t('Request') : `Offer` + ' Name'"/>

        <booking-field-input v-if="!$vlastMode && !booking.is_offer" :booking="booking" :property-mutator="propertyMutator"
                             style="text-transform: capitalize" field="segment" field-type="select"
                             :options="segmentOptions" label="Segment"/>

        <booking-field-input v-if="!$vlastMode && !booking.is_offer" :booking="booking" :property-mutator="propertyMutator" field="organisation_name" v-bind:label="$t('organisation') + ' Name' | sentenceCase"/>

        <booking-field-input v-if="booking.segment === 'agency' && !booking.is_offer" :booking="booking" :property-mutator="propertyMutator" field="agency_stage" :value="agencyStageOptions.findIndex(element => element.id === booking.agency_stage) !== -1 ? agencyStageOptions.find(element => element.id === booking.agency_stage).text : ''" other-field="other_agency_stage" field-type="select" :options="agencyStageOptions" label="Agency Stage"/>
        <booking-field-input
          v-if="userCanSeeBudget && (($userIsTalent || $userIsManager) ? booking.budget_per_star_as_user_rate !== undefined : booking.budget_per_star !== undefined) && !booking.is_offer"
          classes="highlight"
                             :booking="booking"
                             :property-mutator="propertyMutator"
                             :field="$userIsTalent || $userIsManager ? 'budget_per_star_as_user_rate' : 'budget_per_star'"
                             field-type="number"
                             :currency="true"
                             :pre-text="budgetPreText"
                             :post-text="budgetPostText"
                             :highlight="true"
                             :label="budgetLabel">

          <template v-if="booking.local_currency && !booking.is_offer" v-slot:after-info><span @click="trackCurrencyClick">{{ conversionEstimate }}</span></template>
        </booking-field-input>
        <booking-field-input v-if="!booking.is_offer" :booking="booking" :property-mutator="propertyMutator" field="required_stars"
                             field-type="number" :label="$starLabelPlural | sentenceCase"/>

        <booking-field-input v-if="userCanSeeBudget && booking.budget_code && !$isQas && !booking.is_offer"
          :booking="booking"
          :property-mutator="propertyMutator"
          field="budget_code" label="Budget Code"/>
        <booking-field-input v-if="userCanSeeBudget && booking.cost_centre && $isQas && !booking.is_offer"
           :booking="booking"
           :property-mutator="propertyMutator"
           field="cost_centre" label="Cost Centre"/>
        <booking-field-input v-if="userCanSeeBudget && booking.gl_code && $isQas && !booking.is_offer"
           :booking="booking"
           :property-mutator="propertyMutator"
           field="gl_code" label="GL Code"/>
        <booking-field-input
          v-if="(bookingAppearanceInPerson && !booking.date_flexible_or_unconfirmed || (bookingAppearanceOnline && $vlastMode)) && !$allowBookingDateRange"
           :booking="booking"
           :property-mutator="propertyMutator"
           field="utc_date"
           field-type="date"
          :label="(bookingAppearanceOnline && $vlastMode) ?
            booking.is_offer ? 'End Date' : 'Event Date' : 'In Person Event Date'"
          :show-booking-tz="true"/>
        <booking-field-input
          v-else-if="(bookingAppearanceInPerson && !booking.date_flexible_or_unconfirmed || (bookingAppearanceOnline && $vlastMode)) && $allowBookingDateRange"
           :booking="booking"
           :property-mutator="propertyMutator"
           field="date_range"
           :label="(bookingAppearanceOnline && $vlastMode) ? 'Event Date' : 'In Person Event Date'"
           :show-booking-tz="false"
           :show-tz="false"
          :editable="false"/>
        <booking-field-multiple-date v-else-if="bookingAppearanceInPerson && booking.date_flexible_or_unconfirmed && !booking.is_offer"
                                     :booking="booking" :property-mutator="propertyMutator"
                                     label="In Person Event Date Options"/>
        <booking-field-input
          v-if="booking.appearance_type === 'offer'"
           :booking="booking"
           :property-mutator="propertyMutator"
           field="utc_date"
           field-type="date"
           label="End Date"
          :show-booking-tz="true"/>
        <booking-field-input v-if="bookingAppearanceOnline && !$vlastMode && !booking.is_offer" :booking="booking" :property-mutator="propertyMutator"
                             field="complete_by_date" field-type="date" label="Online Deliverables Complete By Date"
                              :show-tz="false" :post-text="booking.client && booking.client.timezone ? `(${booking.client.timezone})` : ''"/>
        <booking-field-input v-if="bookingAppearanceOnline && $vlastMode && !booking.is_offer" :booking="booking" :property-mutator="propertyMutator"
                             field="video_url" field-type="link" label="Video URL" />
        <booking-field-input v-if="bookingAppearanceInPerson && !booking.is_offer" :booking="booking" :property-mutator="propertyMutator"
                             field="arrival_time" field-type="time" label="Arrival Time"/>
        <booking-field-input v-if="(bookingAppearanceInPerson || (bookingAppearanceOnline && $vlastMode)) && !booking.is_offer" :booking="booking" :property-mutator="propertyMutator"
                             field="athlete_start_time" field-type="time" label="Start Time"/>
        <template v-if="(bookingAppearanceInPerson && bookingHasLeaveTime || (bookingAppearanceOnline && $vlastMode)) && !booking.is_offer">
          <booking-field-input :booking="booking" :property-mutator="propertyMutator" field="athlete_leave_time"
                               field-type="time" label="Leave Time"/>
          <booking-field-input :booking="booking" :property-mutator="propertyMutator" field="event_duration"
                               label="Event Duration"/>
        </template>
        <booking-field-input v-if="booking.travel_time && !$isNflClient && !booking.is_offer" :booking="booking" :property-mutator="propertyMutator"
                             field="travel_time" field-type="select" :options="opportunityDurationOptions"
                             label="Travel Time" :value="booking.travel_time_formatted"/>
        <booking-field-input v-if="showDurationField && !booking.is_offer" :booking="booking" :property-mutator="propertyMutator"
                             field="duration" field-type="select" :options="opportunityDurationOptions"
                             label="Speaking Time"/>
        <booking-field-input v-if="!booking.is_offer" :booking="booking" :property-mutator="propertyMutator" :editable="false"
                             field="appearance_type" :value="bookingAppearanceType" label="Appearance Type"/>
        <booking-field-input v-if="booking.package && booking.package.name && !booking.is_offer" :editable="false" :value="booking.package.name" label="Package" :full-width="true" :property-mutator="propertyMutator" field="" :booking="booking"/>
        <!--          TODO: Booking custom fields CU-3judex-->
        <booking-field-input v-if="booking.aca_event_location && !booking.is_offer" :booking="booking" :property-mutator="propertyMutator" field="aca_event_location" label="Event Location"/>
        <booking-field-input v-if="booking.aca_event_distance && !booking.is_offer" :booking="booking" :property-mutator="propertyMutator" field="aca_event_distance" label="Distance"/>
        <booking-field-input v-if="booking.aca_event_gender && !booking.is_offer" :booking="booking" :property-mutator="propertyMutator" field="aca_event_gender" label="Event Gender"/>
        <booking-field-input v-if="booking.aca_number_participants && !booking.is_offer" :booking="booking" :property-mutator="propertyMutator" field="aca_number_participants" label="Number of Participants"/>
        <booking-field-input v-if="booking.aca_participant_ability && !booking.is_offer" :booking="booking" :property-mutator="propertyMutator" field="aca_participant_ability" label="Participant Ability"/>
        <!--          TODO: Booking custom fields CU-3judex-->

        <booking-field-input v-if="!booking.is_offer" :full-width="true" :booking="booking" :property-mutator="propertyMutator"
                             field="opportunities" field-type="multi-select"
                             :multi-select-types="['appearance_type', 'theme', 'event_type', 'opportunity_type']"
                             label="Event Summary"/>

        <booking-field-input :full-width="true" :booking="booking" :property-mutator="propertyMutator"
                             field="description_formatted" field-type="textarea" :label="!booking.is_offer ? $t('Request') + ' Details' : `Offer Details`" html/>
        <booking-field-input v-if="bookingAppearanceInPerson && $showDressCode && !booking.is_offer" :full-width="true" :booking="booking" :property-mutator="propertyMutator" :editable="false"
                             field="dress_code" label="Dress Code"/>
        <booking-field-input v-if="booking.requirements && !booking.is_offer" :full-width="true" :booking="booking"
                             :property-mutator="propertyMutator" field="requirements_formatted" field-type="textarea"
                             :label="$t('Request') + ' Requirements'" html>
          <p slot="after-info" class="requirements-info" v-if="displayMarketingDisclaimer">
            Important: When talent are required to complete a promotional or branded post on their social media
            accounts, it is likely this will need to be disclosed as advertising (e.g. #ad or #spon). To find out if
            this is relevant to you, read more <a target="_blank"
                                                  :href="socialMediaDisclosureLink">here</a>
            or email <a :href="'mailto:' + $user.region.admin_email">{{ $user.region.admin_email }}</a> if you have any questions.
          </p>
        </booking-field-input>
        <template v-if="bookingAppearanceInPerson">
          <booking-field-input :full-width="true" :booking="booking" :property-mutator="propertyMutator"
                               field="location" :value="booking.address" field-type="location" label="Location"/>
          <booking-field-input :full-width="true" :booking="booking" :property-mutator="propertyMutator"
                               field="additional_location_detail" label="Additional Location Detail"/>
        </template>

        <booking-field-input v-if="$categoryModuleEnabled && !booking.is_offer" :booking="booking" :property-mutator="propertyMutator"
                             field="category_id" field-type="select" :options="categoryOptions"
                             :value="selectedCategory"
                             :label="$t('Request Category')"/>

        <booking-field-input v-if="$vlastMode && $isNrlClub && $showDepartmentField && !booking.is_offer" :booking="booking" :property-mutator="propertyMutator"
                                    field="department_id" field-type="select" :options="categoryOptions"
                                    :value="booking.department ? booking.department.name : ''"
                                    v-bind:label="$t('requesting department') | sentenceCase"/>

        <booking-field-input v-if="$vlastMode && $isNflClient && !booking.is_offer" :booking="booking"
                             :property-mutator="propertyMutator" field="nfl_event_type" label="NFL Event"/>

        <booking-field-input v-if="$userIsAdmin" :booking="booking" :property-mutator="propertyMutator"
                             :editable="false" field-type="date" field="created_at" label="Created Date"/>
        <booking-field-input v-if="!booking.is_offer && $userIsAdmin" :booking="booking" :property-mutator="propertyMutator"
                             :editable="false" field-type="date" field="vetted_date" label="Vetted Date"/>
        <booking-field-input v-if="!booking.is_offer && !bookingTypeIsBookingRequestOrDraft && $userIsAdmin" :booking="booking"
                             :property-mutator="propertyMutator" :editable="false" field-type="date"
                             field="scheduled_date" label="Booked Date"/>
        <booking-field-input v-if="!bookingTypeIsBookingRequestOrDraft && $userIsAdmin && bookingIsCompleted"
                             :booking="booking" :property-mutator="propertyMutator" :editable="false" field-type="date"
                             field="completed_date" label="Completed Date"/>

        <div class="form-item__field" v-if="$userIsAdmin && !booking.is_offer">
          <div class="input-group input-group-booking-detail">
            <span class="input-group-addon">Assigned Admin</span>
            <selectize
              v-if="audience.length"
              class="selectize selectize-single"
              name="marketing_source_admin"
              :disable-delete="true"
              :placeholder="'Unassigned'"
              v-model="stateAssignedAdmin"
              :options="audience">
            </selectize>
          </div>
        </div>

        <div class="form-item__field" v-if="$userIsAdmin && !this.$vlastMode && !booking.is_offer">
          <div class="input-group input-group-booking-detail">
            <span class="input-group-addon">Revenue Category</span>
            <selectize
              class="selectize selectize-single"
              name="revenue_category"
              :disable-delete="true"
              :disabled="this.booking.type !== 'request'"
              v-model="stateRevenueCategory"
              :options="revenueCategoryOptions">
            </selectize>
          </div>
        </div>

        <template v-if="this.$vlastMode && booking.custom_fields && !booking.is_offer" >
          <booking-field-input v-for="(field, index) in booking.custom_fields" :key="field.name" :full-width="true" :booking="booking" :property-mutator="propertyMutator"
                               :field="field.name" :value="field.value" :field-type="field.dataType ? field.dataType : 'text'" :label="booking.custom_fields[index].name" :options="{'fileUrl': field.file_url}"/>
        </template>

        <div class="more-details w-full spacer-t-4" v-if="!booking.is_offer" >
          <booking-field-boolean v-if="!$isNflClient" :booking="booking" :property-mutator="propertyMutator" field="location_unconfirmed"
                                 label="Location Unconfirmed"/>
          <booking-field-boolean v-if="!$isNflClient" :booking="booking" :property-mutator="propertyMutator"
                                 field="date_flexible_or_unconfirmed" label="Flexible Date"/>
          <booking-field-boolean v-if="!$isNflClient" :booking="booking" :property-mutator="propertyMutator"
                                 field="time_flexible_or_unconfirmed" label="Flexible Time"/>
          <booking-field-boolean :booking="booking" :property-mutator="propertyMutator" field="expenses_covered"
                                 label="Travel and Accommodation"/>
          <booking-field-boolean v-if="!$hidePublicPromotingStatus" :booking="booking" :property-mutator="propertyMutator" field="promoted_publicly"
                                 label="Public Promoting"/>
          <booking-field-boolean v-if="!$isNflClient" :booking="booking" :property-mutator="propertyMutator" field="budget_not_confirmed"
                                 :label="$t('budget') + ' Unconfirmed' | sentenceCase"/>
          <booking-field-boolean v-if="$billboardModuleEnabled" :booking="booking" :property-mutator="propertyMutator" :editable="$userIsAdmin"
                                 field="others_can_apply" label="Others Can Apply"/>
        </div>
      </div>

      <booking-field-input v-if="($userIsAdmin || $userIsClient) && bookingHasCoupon && !booking.is_offer" :booking="booking" :property-mutator="propertyMutator" field="coupon" label="Coupon Code"/>

      <div class="event-map" v-if="bookingAppearanceInPerson && bookingLatitudeAndLongitude && !booking.is_offer">
        <gmap-map
          :center="bookingLatitudeAndLongitude"
          :zoom="10"
          ref="eventMap">
          <gmap-marker
            :position="bookingLatitudeAndLongitude"/>
        </gmap-map>
      </div>
      <div v-if="fieldSetSelected('video_message') && !booking.is_offer">
        <h2>Video Message Details</h2>
        <booking-field-input :booking="booking" :property-mutator="propertyMutator" field="video_caption_formatted"
                             field-type="textarea" label="Video Content" :full-width="true" html/>
        <div v-if="!booking.commercial_purposes">
          <booking-field-input :booking="booking" :property-mutator="propertyMutator" field="video_message_first_name"
                               label="For (first name)"/>
          <booking-field-input :booking="booking" :property-mutator="propertyMutator" field="video_message_last_name"
                               label="For (last name)"/>
        </div>
        <booking-field-checkboxes v-if="booking.commercial_purposes" :booking="booking"
                                  :property-mutator="propertyMutator" field="video_message_places"
                                  other-field="other_video_usage" :other-value="videoMessageUsageLocationOptions[2].id"
                                  field-type="select" :options="videoMessageUsageLocationOptions"
                                  label="Usage Locations"/>
        <booking-field-input v-if="booking.commercial_purposes" :booking="booking" :property-mutator="propertyMutator"
                             field="video_rights_length" field-type="select" :options="rightsLengthOptions"
                             label="Rights Length" :full-width="true"/>
        <div class="more-details">
          <booking-field-boolean :booking="booking" :property-mutator="propertyMutator" field="commercial_purposes"
                                 label="Commercial Purposes"/>
        </div>
      </div>
      <div v-if="fieldSetSelected('social_post') && !booking.is_offer">
        <h2>Social Post Details</h2>
        <booking-field-input :booking="booking" :property-mutator="propertyMutator" field="post_caption_formatted"
                             field-type="textarea" label="Social Content" html>
          <p slot="after-info" class="requirements-info" v-if="displayMarketingDisclaimer">
            Important: When talent are required to complete a promotional or branded post on their social media
            accounts, it is likely this will need to be disclosed as advertising (e.g. #ad or #spon). To find out if
            this is relevant to you, read more <a target="_blank"
                                                  :href="socialMediaDisclosureLink">here</a>
            or email <a :href="'mailto:' + $user.region.admin_email">{{ $user.region.admin_email }}</a> if you have any questions.
          </p>
        </booking-field-input>
        <booking-field-input :booking="booking" :property-mutator="propertyMutator" field="number_of_social_media_posts"
                             field-type="number" label="Number of Social Media Posts"/>
        <booking-field-input :booking="booking" :property-mutator="propertyMutator" field="social_post_location"
                             field-type="select" :options="socialLocationOptions" label="Social Post Location"/>
        <booking-field-input v-if="booking.paid_advertising" :booking="booking" :property-mutator="propertyMutator"
                             field="rights_length" field-type="select" :options="socialLengthOptions"
                             label="Rights Length"/>
        <div v-if="showPaidAdvertising" class="more-details">
          <booking-field-boolean :booking="booking" :property-mutator="propertyMutator" field="paid_advertising" label="Used In Paid Advertising"/>
        </div>
      </div>
      <div v-if="fieldSetSelected('ambassador') && !booking.is_offer">
        <h2>Brand Ambassador Details</h2>
        <booking-field-input :booking="booking" :property-mutator="propertyMutator" field="ambassadorship_details_formatted"
                             field-type="textarea" label="Ambassadorship Details" html/>
        <booking-field-input :booking="booking" :property-mutator="propertyMutator" field="target_market"
                             label="Target Market"/>
        <booking-field-input :booking="booking" :property-mutator="propertyMutator" field="ambassador_length"
                             field-type="select" :options="ambassadorLengthOptions" label="Ambassador Length"/>
        <booking-field-input :booking="booking" :property-mutator="propertyMutator" field="commencement_date"
                             field-type="date" label="Commencement Date"/>
        <template v-if="fieldSetSelected('marketing_campaign')">
          <booking-field-input :booking="booking" :property-mutator="propertyMutator" field="marketing_channels"
                               field-type="multi-select" :multi-select-types="['marketing_channel']"
                               label="Marketing Channels"/>

          <div class="more-details">
            <booking-field-boolean :booking="booking" :property-mutator="propertyMutator" field="shoot_required"
                                   label="Shoot Required"/>
          </div>
        </template>
      </div>
    </div>
</template>

<script>
import { helpers } from 'mixins/bookings'
import Selectize from '../ext/codium/CodiumSelectize'
import { alert, notify } from 'utils'
import { mapActions, mapState } from 'vuex'
import BookingTalentRecommendations from './BookingTalentRecommendations'
import BookingFieldInput from './BookingFieldInput'
import BookingFieldBoolean from './BookingFieldBoolean'
import { BookingStateMapper as stateMapper } from 'pickstar-common'
import BookingFieldMultipleDate from './BookingFieldMultipleDate'
import BookingFieldCheckboxes from './BookingFieldCheckboxes'
import { map } from 'lodash'
import getSymbolFromCurrency from 'currency-symbol-map'
import { OpportunityTracker } from 'mixins/analytics'

export default {
  mixins: [helpers, stateMapper, OpportunityTracker],

  data () {
    return {
      audience: [],
      prevAssignedAdmin: null,
      downloading: false,
      categoryOptions: [],
      ambassadorLengthOptions: [
        { id: '< 3 Months', text: '< 3 Months' },
        { id: '3 Months', text: '3 Months' },
        { id: '6 Months', text: '6 Months' },
        { id: '12 Months', text: '12 Months' },
        { id: '> 12 Months', text: '> 12 months' }
      ],
      rightsLengthOptions: [
        { id: '1 Month', text: '1 Month' },
        { id: '6 Months', text: '6 Months' },
        { id: '1 Year', text: '1 Year' },
        { id: '2 Years', text: '2 Years' }
      ],
      socialLengthOptions: [
        { id: '< 3 Months', text: '< 3 Months' },
        { id: '3 Months', text: '3 Months' },
        { id: '6 Months', text: '6 Months' },
        { id: '12 Months', text: '12 Months' },
        { id: '> 12 Months', text: '> 12 Months' },
        { id: 'Undecided', text: 'Undecided' }
      ],
      agencyStageOptions: [
        {
          'id': 'Brief',
          'text': 'I am responding to a brief'
        },
        {
          'id': 'Proactive',
          'text': 'I am doing a proactive'
        },
        {
          'id': 'Seeking talent',
          'text': 'Have approval and seeking talent'
        },
        {
          'id': 'Other',
          'text': 'Other'
        }
      ],
      opportunityDurationOptions: [
        { id: '15 Minutes', text: '15 Minutes' },
        { id: '30 Minutes', text: '30 Minutes' },
        { id: '1 Hour', text: '1 Hour' },
        { id: '1.5 Hours', text: '1.5 Hours' },
        { id: '2 Hours', text: '2 Hours' },
        { id: '2.5 Hours', text: '2.5 Hours' },
        { id: '3 Hours', text: '3 Hours' },
        { id: '3.5 Hours', text: '3.5 Hours' },
        { id: '4 Hours', text: '4 Hours' },
        { id: 'Full Day', text: 'Full Day' }
      ],
      segmentOptions: [
        { id: 'agency', text: 'Agency' },
        { id: 'association', text: 'Association' },
        { id: 'brand', text: 'Brand' },
        { id: 'business', text: 'Business' },
        { id: 'club', text: 'Club' },
        { id: 'fan', text: 'Fan' },
        { id: 'school', text: 'School' }
      ],
      videoMessageUsageLocationOptions: [
        { id: 'Website', text: 'Website' },
        { id: 'Social Media', text: 'Social Media' },
        { id: 'Other Marketing Materials', text: 'Other Marketing Materials' }
      ]
    }
  },

  components: {
    BookingFieldCheckboxes,
    BookingFieldMultipleDate,
    Selectize,
    BookingTalentRecommendations,
    BookingFieldInput,
    BookingFieldBoolean
  },

  props: {
    booking: {
      type: Object,
      required: true
    },
    propertyMutator: {
      type: Function,
      required: true
    }
  },

  methods: {
    ...mapActions('booking', ['revenueCategory', 'assignAdmin', 'getPdf', 'updateBooking', 'setBookingProperty']),
    ...mapActions('user', ['getAdminUsers']),
    ...mapActions('editing', ['setEditing']),
    ...mapActions('booking/request', ['updateBookingRequest', 'setRequestProperty']),
    ...mapActions('booking/request/vetting', ['updateVettingBookingRequest', 'setVettingRequestProperty']),
    ...mapActions('category', ['getCategories']),

    trackCurrencyClick () {
      this.trackEvent(this.analytics.V2.CurrencyConversionInfoClickEvent, { pageName: this.$route.name, clientSegment: this.booking.segment })
    },

    mapOpportunitiesResponseForSelectize (opportunities) {
      return map(opportunities, (opportunity) => ({ id: opportunity.id, text: opportunity.name }))
    },

    toggleEditing () {
      if (this.userCanEdit) {
        this.setEditing(!this.editing)

        if (this.editing === false) {
          let payload = this.booking
          payload = {
            ...payload,
            establishment: this.stateEstablishment,
            street_address: this.stateStreetAddress,
            suburb: this.stateSuburb,
            postcode: this.statePostcode,
            area: this.stateArea,
            state: this.stateState,
            country: this.stateCountry,
            latitude: this.stateLatitude,
            longitude: this.stateLongitude
          }
          if (this.booking.type === 'booking') {
            this.updateBooking({
              id: this.booking.id,
              payload
            })
          } else if (this.booking.status === 'vetting' && this.$userIsAdmin) {
            this.updateVettingBookingRequest({
              id: this.booking.id,
              payload
            })
          } else {
            this.updateBookingRequest({
              id: this.booking.id,
              payload
            })
          }
        }
      }
    },

    handleDownloadPdfCallback () {
      this.downloading = true
      this.trackEvent(this.analytics.V2.JobPdfDownloadEvent)
      return this.getPdf({ id: this.booking.id })
        .then(({ data: { file, name } }) => {
          window.open(`${file}?name=${name}`, '_blank')
          this.downloading = false
        })
        .catch(() => {
          this.downloading = false
        })
    }
  },

  computed: {
    ...mapState('editing', ['editing']),

    revenueCategoryOptions () {
      return [
        { id: 'inbound', text: 'Inbound' },
        { id: 'inbound-upsold', text: 'Inbound - Upsold' },
        { id: 'outbound', text: 'Outbound' }
      ]
    },

    socialMediaDisclosureLink () {
      switch (this.$user.region.shortname) {
        case 'us':
          return 'https://www.ftc.gov/tips-advice/business-center/guidance/ftcs-endorsement-guides-what-people-are-asking '
        default:
          return 'http://aana.com.au/content/uploads/2018/10/AANA_Clearly-Distinguishable-Advertising_2018-2.pdf'
      }
    },

    selectedCategory () {
      if (this.booking.category_id) {
        const category = this.categoryOptions.find(element => element.id === this.booking.category_id)
        if (category) {
          return category.text
        }
      }
      return null
    },

    socialLocationOptions () {
      return [
        {
          id: this.$options.filters.sentenceCase(this.$starLabel) + '(s) profile',
          text: this.$options.filters.sentenceCase(this.$starLabel) + '(s) profile - ' + this.$options.filters.sentenceCase(this.$starLabel) + '(s) will share the content'
        },
        {
          id: 'Our pages',
          text: 'Our pages - We will share the content directly'
        },
        {
          id: 'Both',
          text: 'Both'
        }
      ]
    },

    localCurrencySymbol () {
      if (this.booking.local_currency) {
        return getSymbolFromCurrency(this.booking.local_currency)
      }
      return null
    },

    getCurrency () {
      if (this.booking.currency === 'points') {
        return ''
      }
      if (this.booking.region && typeof this.booking.region.currency_code !== 'undefined') {
        return getSymbolFromCurrency(this.booking.currency)
      }
      return ''
    },
    userCanEdit () {
      return this.$userIsAdmin || (this.$userIsClient && this.booking.status === 'vetting')
    },

    bookingHasLeaveTime () {
      return this.booking.athlete_leave_time
    },

    showDurationField () {
      return this.booking.opportunities.findIndex(item => item.optional_fields.includes('duration') === true) !== -1
    },

    showPaidAdvertising () {
      return this.booking.social_post_location === 'Our pages' || this.booking.social_post_location === 'Both'
    },

    budgetLabel () {
      if (this.booking.currency === 'points') {
        return 'Points'
      }
      return this.$userIsTalent || this.$userIsManager ? 'Your Payment' : this.$options.filters.sentenceCase(this.$t('budget'))
    },

    budgetPreText () {
      if (this.booking.currency === 'points') {
        return ''
      }
      return this.booking.currency + ' ' + this.getCurrency
    },

    budgetPostText () {
      let postText = this.booking.currency === 'points' ? ' Points' : ''
      if (this.$userIsManager) {
        postText += ` /${this.$starLabel}`
      }
      if (this.$userIsClient) {
        postText += ` /${this.$starLabel}`
        if (this.$showTax) {
          postText += ` (+tax)`
        }
      }
      if (this.$userIsAdmin) {
        if (this.booking.currency !== 'points') {
          postText += ` (${this.getCurrency + this.booking.budget_per_star_as_user_rate.toFixed(2)}) /${this.$starLabel}`
          if (this.$showTax) {
            postText += ` (+tax)`
          }
        } else {
          postText += ` (${this.booking.budget_per_star_as_user_rate.toFixed(2)} Points) /${this.$starLabel}`
        }
      }
      if (this.booking.type === 'request' && this.booking.budget_not_confirmed) {
        postText += ' (not-confirmed)'
      }
      return postText
    },

    conversionEstimate () {
      let postText = ''
      if (this.booking.local_currency && (this.booking.local_currency_budget_per_star || this.booking.local_currency_budget_per_star_as_user_rate)) {
        if (this.$userIsAdmin) {
          postText += `${this.booking.local_currency} ${this.localCurrencySymbol}${this.booking.local_currency_budget_per_star.toFixed(2)} (${this.localCurrencySymbol}${this.booking.local_currency_budget_per_star_as_user_rate.toFixed(2)}) /star`
          if (this.$showTax) {
            postText += ` (+tax)`
          }
        } else {
          postText += `${this.booking.local_currency} ${this.localCurrencySymbol}${this.booking.local_currency_budget_per_star_as_user_rate.toFixed(2)}`
        }
        postText += ' *estimate'
      }
      return postText
    },

    bookingHasCoupon () {
      return this.booking.coupon !== null
    },

    assignedAdminId () {
      return this.booking.assigned_admin
    },

    stateAssignedAdmin: {
      get () {
        return this.booking.assigned_admin
      },

      set (value) {
        this.propertyMutator({ property: 'assigned_admin', value })
      }
    },

    stateRevenueCategory: {
      get () {
        return this.booking.revenue_category
      },

      set (value) {
        this.propertyMutator({ property: 'revenue_category', value })
      }
    },

    displayMarketingDisclaimer () {
      if (this.booking.package_id === 1) {
        return false
      }
      return this.bookingAppearanceOnline
    }
  },

  mounted () {
    if (this.bookingAppearanceInPerson && this.bookingLatitudeAndLongitude !== null) {
      this.$nextTick(() => {
        if (this.$refs.eventMap) {
          this.$refs.eventMap.resizePreserveCenter()
        }
      })
    }

    // Setup the dropdown options if user is admin
    if (this.$userIsAdmin) {
      this.getAdminUsers()
        .then((response) => {
          response.data.forEach(user => this.audience.push({ id: user.id, text: user.name }))
        })
    }

    if (this.$vlastMode && this.$categoryModuleEnabled) {
      this.getCategories().then(({ data }) => {
        this.categoryOptions = this.mapOpportunitiesResponseForSelectize(data)
      })
    }
  },

  watch: {
    stateAssignedAdmin (assignedAdmin, prevAssignedAdmin) {
      if (assignedAdmin === this.prevAssignedAdmin) {
        return
      }
      this.prevAssignedAdmin = prevAssignedAdmin

      const confirmAlertOptions = {
        icon: 'warning',
        dangerMode: false,
        title: this.$t('Request') + '/Booking Assignment',
        text: `Are you sure you want to assign this to ${this.audience.find(user => user.id === assignedAdmin).text}?`,
        buttons: {
          confirm: {
            text: 'Yes'
          }
        }
      }

      return alert.confirm(confirmAlertOptions)
        .then(() => {
          this.assignAdmin({ bookingId: this.booking.id, userId: this.stateAssignedAdmin })
          notify.success('Successfully assigned admin')
        })
        .catch(() => {
          this.stateAssignedAdmin = this.prevAssignedAdmin
        })
    },
    stateRevenueCategory (category) {
      this.revenueCategory({ bookingId: this.booking.id, revenueCategory: category })
    }
  }
}
</script>

<style scoped lang="scss">
.edit-container {
  position: sticky;
  top: 50px;
  z-index: 10;
  display: flex;
  justify-content: flex-end;
  button {
    height: 30px;
    width: 30px;
    border-radius: 50%;
    @media(min-width: 1100px) {
      transform: translateX(50px);
    }
  }
}

.more-details span.option {
  padding-top: 10px;
  display: inline-block;
}
</style>
